<template>
  <StackChart
    ref="chart"
    :unit="unit"
    :uuid="uuid"
    :scale="scale"
    :loading="loading"
    :title="widgettitle"
    :daterange="daterange"
  />
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import StackChart from '../StackChart.vue';
import {
  buildDowntimeDepartmentDisplayOrder,
  getDowntimeCategoryFromRow,
  getDowntimeCategoryColor,
  getDowntimeDepartmentValue,
} from '../../utils/downtime-colors';

export default {
  name: 'MachineDowntimeYesterday',
  props: {
    widgettitle: { type: String },
    scale: { type: Number, default: 1 },
    unit: { type: String },
  },
  components: { StackChart },
  data() {
    return {
      title: 'Widget', loading: false, uuid: '', daterange: '', currentshifttime: null,
    };
  },
  computed: {
    ...mapState('cellwiseDowntimeDashboard', [
      'shifts', 'serverTime', 'dataFromJSON', 'machines', 'downtimeReasons', 'currentDate', 'config', 'theme',
    ]),
    ...mapGetters('cellwiseDowntimeDashboard', ['firstShift']),
    cellname() { return this.$route.query.selectedcell; },
  },
  watch: {
    cellname() { this.fetchData(); },
    currentDate(newVal, oldVal) {
      if (newVal && newVal !== oldVal && !this.dataFromJSON) this.fetchData();
    },
    'theme.isDark': function themeIsDark() {
      if (this.dataFromJSON) this.dummydata();
      else this.fetchData();
    },
  },
  mounted() {
    if (this.dataFromJSON) this.dummydata();
    else this.fetchData();
  },
  methods: {
    ...mapActions('cellwiseDowntimeDashboard', [
      'allShifts', 'getReports', 'getFileData', 'getMachines', 'fetchDowntimeReasons',
    ]),

    getSiteId() { return this.$store.getters['cellwiseDowntimeDashboard/activeSite']; },

    parseBusinessDate(yyyymmdd) {
      const dateStr = yyyymmdd.toString();
      const year = parseInt(dateStr.substring(0, 4), 10);
      const month = parseInt(dateStr.substring(4, 6), 10) - 1;
      const day = parseInt(dateStr.substring(6, 8), 10);
      return new Date(year, month, day);
    },

    getYYYYMMDD(dateObj) {
      return parseInt(
        `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}${String(dateObj.getDate()).padStart(2, '0')}`,
        10,
      );
    },

    async dummydata() {
      const res = await this.getFileData('jsondata/machinewisedowntimeyesterday.json');
      const baseTime = this.serverTime ? new Date(this.serverTime).setHours(0, 0, 0, 0)
        : new Date().setHours(0, 0, 0, 0);
      const yesterdayStart = baseTime - 86400000;
      this.setTitle(yesterdayStart);
      if (this.$refs.chart && res) this.$refs.chart.assignData(res);
    },
    async fetchData() {
      if (this.loading) return;
      this.loading = true;

      try {
        if (!this.currentDate) {
          this.setZeroState();
          return;
        }

        const baseDate = this.parseBusinessDate(this.currentDate);
        baseDate.setDate(baseDate.getDate() - 1);

        const targetYYYYMMDD = this.getYYYYMMDD(baseDate);
        this.setTitle(baseDate.getTime());

        const activeSite = this.getSiteId();
        if (!activeSite) {
          this.setZeroState();
          return;
        }

        const payload = {
          start: targetYYYYMMDD,
          end: targetYYYYMMDD,
          siteid: activeSite,
        };
        const reportName = 'dailymachinedowntime';
        const result = await this.getReports({ reportName, payload });

        if (result) {
          let parsed = typeof result === 'string' ? JSON.parse(result) : result;
          if (typeof parsed === 'string') {
            try { parsed = JSON.parse(parsed); } catch (e) { console.warn(e); }
          }

          let chartData = null;
          let actualArray = null;

          if (Array.isArray(parsed)) actualArray = parsed;
          else if (parsed && Array.isArray(parsed.reportData)) actualArray = parsed.reportData;
          else if (parsed && Array.isArray(parsed.data)) actualArray = parsed.data;

          if (actualArray && actualArray.length > 0 && this.cellname) {
            if (!this.machines || this.machines.length === 0) await this.getMachines();
            let validMachineNames = [];
            if (this.machines && this.machines.length > 0) {
              validMachineNames = this.machines
                .filter((m) => m.machinecell === this.cellname)
                .map((m) => m.machinename);
            }
            if (validMachineNames.length > 0) {
              actualArray = actualArray.filter((row) => {
                const mName = row.machinename || row.machine || row.assetname || '';
                return validMachineNames.includes(mName);
              });
            } else actualArray = [];
          }

          if (parsed && parsed.series && parsed.series.length > 0) {
            chartData = parsed;
          } else if (actualArray && actualArray.length > 0) {
            if (!this.downtimeReasons || this.downtimeReasons.length === 0) {
              if (typeof this.fetchDowntimeReasons === 'function') {
                try { await this.fetchDowntimeReasons(); } catch (e) { /* Ignore */ }
              }
            }

            const categoriesSet = new Set();
            const deptMap = {};
            const safeReasons = Array.isArray(this.downtimeReasons) ? this.downtimeReasons : [];
            const activeTheme = this.$vuetify.theme.dark ? 'dark' : 'light';

            actualArray.forEach((row) => {
              const mName = row.machinename || row.machine || row.assetname || 'Unknown';
              let dTime = 0;
              if ('totaldowntimeinmins' in row) dTime = row.totaldowntimeinmins;
              else if ('downtime' in row) dTime = row.downtime;
              const val = Number(dTime) || 0;
              const cat = getDowntimeCategoryFromRow(row, safeReasons, 'other');

              categoriesSet.add(mName);
              if (!deptMap[cat]) deptMap[cat] = {};
              if (!deptMap[cat][mName]) deptMap[cat][mName] = 0;
              deptMap[cat][mName] += val;
            });

            const categories = Array.from(categoriesSet).sort();
            const series = [];
            const visibleCategories = buildDowntimeDepartmentDisplayOrder(
              actualArray,
              safeReasons,
              6,
            );
            const departmentOrder = visibleCategories;

            visibleCategories.forEach((catName) => {
              const departmentValues = getDowntimeDepartmentValue(deptMap, catName) || {};
              const dataArr = categories.map((m) => Number((departmentValues[m] || 0).toFixed(2)));
              series.push({
                name: catName,
                data: dataArr,
                color: getDowntimeCategoryColor(
                  this.config,
                  catName,
                  activeTheme,
                  departmentOrder,
                ),
              });
            });

            if (categories.length > 0) chartData = { categories, series };
          }

          if (this.$refs.chart && chartData) this.$refs.chart.assignData(chartData);
          else this.setZeroState();
        } else this.setZeroState();
      } catch (e) {
        this.setZeroState();
      } finally {
        this.loading = false;
      }
    },

    setZeroState() {
      if (this.$refs.chart) this.$refs.chart.assignData({ categories: ['-'], series: [] });
    },

    setTitle(start, end) {
      if (start && end && start !== end) this.daterange = `${this.dateFormat(start)} - ${this.dateFormat(end)}`;
      else this.daterange = this.dateFormat(start);
    },

    dateFormat(val) {
      if (!val) return '';
      try {
        const date = new Date(val);
        if (Number.isNaN(date.getTime())) return '';
        const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
        const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
        const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(date);
        return `${day}/${month}/${year}`;
      } catch (e) { return ''; }
    },
  },
};
</script>
